import { useEffect, useState } from 'preact/hooks';
import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '../shared/protocol.js';

export type ArenaSocket = Socket<ServerToClientEvents, ClientToServerEvents>;
export type ConnectionState = 'connecting' | 'connected' | 'disconnected';

export interface Realtime {
  socket: ArenaSocket | null;
  state: ConnectionState;
  latency: number | null;
  /** serverTime ≈ Date.now() + clockOffset */
  clockOffset: number;
}

/**
 * Opens the realtime connection while the user is logged in. If the server closes the
 * connection (e.g. session revoked), onServerDisconnect lets the app re-check the login.
 */
export function useRealtime(enabled: boolean, onServerDisconnect: () => void): Realtime {
  const [socket, setSocket] = useState<ArenaSocket | null>(null);
  const [state, setState] = useState<ConnectionState>('connecting');
  const [latency, setLatency] = useState<number | null>(null);
  const [clockOffset, setClockOffset] = useState(0);

  useEffect(() => {
    if (!enabled) return;
    const s: ArenaSocket = io({ path: '/socket.io/' });
    setSocket(s);
    setState('connecting');

    const measure = () => {
      if (!s.connected) return;
      const sent = Date.now();
      s.emit('ping', ({ serverTime }) => {
        const received = Date.now();
        const rtt = received - sent;
        setLatency(rtt);
        setClockOffset(serverTime + rtt / 2 - received);
      });
    };

    s.on('connect', () => {
      setState('connected');
      measure();
    });
    s.on('disconnect', (reason) => {
      setState('disconnected');
      // The server closed the connection (session revoked or account changed):
      // re-check the login and reconnect; an invalid session is then rejected.
      if (reason === 'io server disconnect') {
        onServerDisconnect();
        setTimeout(() => s.connect(), 1000);
      }
    });
    s.on('connect_error', (err) => {
      setState('disconnected');
      if (err.message === 'unauthorized') onServerDisconnect();
    });
    const timer = setInterval(measure, 10_000);

    return () => {
      clearInterval(timer);
      s.close();
      setSocket(null);
    };
  }, [enabled]);

  return { socket, state, latency, clockOffset };
}
